import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TableState } from '@optimus-web/core';
import {
    getRepositoryQueryKey,
    SettlementRuleParameters,
    SettlementRuleUpdateParameters,
    SingleSettlementRuleParameters,
    useRepositoryQuery,
    useRepositoryRegistry,
    useRepositorySuspenseQuery,
} from '@mews-commander/data-access';
import { lt, showSuccessAlert } from '@mews/framework';

export const useSettlementRulesGetOverviewEntriesQuery = (tableState: TableState<SettlementRuleParameters>) =>
    useRepositoryQuery('settlementRules', 'getOverviewEntries', tableState);

export const useSettlementRuleGetQuery = (settlementRuleId?: string) =>
    useRepositoryQuery(
        'settlementRules',
        'getAll',
        {
            settlementRuleIds: settlementRuleId ? [settlementRuleId] : [],
            extent: {
                rateGroups: true,
            },
        },
        {
            select: (settlementRules) => settlementRules.find(({ id }) => id === settlementRuleId),
            enabled: !!settlementRuleId,
        }
    );

export const useSettlementRuleGetSuspenseQuery = (settlementRuleId: string) =>
    useRepositorySuspenseQuery(
        'settlementRules',
        'getAll',
        { settlementRuleIds: [settlementRuleId] },
        {
            select: (settlementRules) => settlementRules.find(({ id }) => id === settlementRuleId),
        }
    );

export const useSettlementRulesOverviewEntriesInvalidator = () => {
    const queryClient = useQueryClient();
    return () => {
        void queryClient.invalidateQueries({
            queryKey: getRepositoryQueryKey('settlementRules', 'getOverviewEntries'),
        });
    };
};

export const useSettlementRuleInvalidator = () => {
    const queryClient = useQueryClient();

    return (settlementRuleId: string) => {
        void queryClient.invalidateQueries({
            queryKey: getRepositoryQueryKey('settlementRules', 'getAll', {
                settlementRuleIds: [settlementRuleId],
            }),
        });
    };
};

export const useDeleteSettlementRule = () => {
    const { settlementRules } = useRepositoryRegistry();
    const invalidateSettlementRules = useSettlementRulesOverviewEntriesInvalidator();

    return useMutation({
        mutationFn: async ({ id }: SingleSettlementRuleParameters) => settlementRules.delete({ id }),
        onSuccess: () => {
            invalidateSettlementRules();
            showSuccessAlert({
                title: lt('PaymentPolicyDeletedSuccessMessage'),
            });
        },
    });
};

export const useCreateSettlementRule = () => {
    const { settlementRules } = useRepositoryRegistry();
    const invalidateSettlementRules = useSettlementRulesOverviewEntriesInvalidator();

    return useMutation({
        mutationFn: async (params: SettlementRuleParameters) => settlementRules.add(params),
        onSuccess: () => {
            invalidateSettlementRules();
            showSuccessAlert({
                title: lt('PaymentPolicyCreatedSuccessMessage'),
            });
        },
    });
};

export const useSettlementRuleMappedEntitiesQuery = ({ id }: Partial<Pick<SingleSettlementRuleParameters, 'id'>>) =>
    useRepositoryQuery('settlementRules', 'getMappedEntities', { id: id ?? '' }, { enabled: !!id });

export const useSettlementRuleMappedEntitiesSuspenseQuery = ({ id }: Pick<SingleSettlementRuleParameters, 'id'>) =>
    useRepositorySuspenseQuery('settlementRules', 'getMappedEntities', { id });

export const useUpdateSettlementRule = () => {
    const { settlementRules } = useRepositoryRegistry();

    const invalidateSettlementRules = useSettlementRulesOverviewEntriesInvalidator();
    const invalidateSettlementRule = useSettlementRuleInvalidator();

    return useMutation({
        mutationFn: async (params: SettlementRuleUpdateParameters) => settlementRules.update(params),
        onSuccess: (_, { id }) => {
            invalidateSettlementRules();
            invalidateSettlementRule(id);
            showSuccessAlert({
                title: lt('PaymentPolicyUpdatedSuccessMessage'),
            });
        },
    });
};
